import type { CSSProperties, JSX, ReactNode } from "react";
import { cx } from "../cx";

export type AppShellNavMode = "sidebar" | "rail";

export interface StudioNavItem {
  id: string;
  /** Item name — visible text in sidebar mode, the accessible name in rail mode. */
  label: string;
  /** Lucide icon at 16px, stroke currentColor, aria-hidden. */
  icon?: ReactNode | undefined;
  /** Marks the current destination (blue-tinted surface, never volt). */
  active?: boolean | undefined;
  onSelect?: (() => void) | undefined;
}

export interface StudioNavGroup {
  /** Caps-tracked group label, shown in sidebar mode only. */
  label?: string | undefined;
  /** Stable key when the label is omitted or repeated. */
  id?: string | undefined;
  items: ReadonlyArray<StudioNavItem>;
}

export interface AppShellProps {
  /** Brand slot at the far left of the top bar (mark SVG at 16-20px). */
  brand?: ReactNode | undefined;
  /** Flexible middle of the top bar (breadcrumb, status text, live dot). */
  center?: ReactNode | undefined;
  /** Right side of the top bar (IconButtons, avatar). */
  actions?: ReactNode | undefined;
  /** `sidebar` = 224px labeled column (default); `rail` = 56px icon rail. */
  navMode?: AppShellNavMode | undefined;
  /** Grouped nav items; group labels render caps-tracked in sidebar mode. */
  nav?: ReadonlyArray<StudioNavGroup> | undefined;
  /** Accessible name for the nav landmark. */
  navLabel?: string | undefined;
  /** Docked right panel, usually an InspectorPanel; wrapped in an aside. */
  inspector?: ReactNode | undefined;
  children?: ReactNode | undefined;
  className?: string | undefined;
  style?: CSSProperties | undefined;
}

/**
 * Studio application chrome: fixed top bar (--studio-topbar-h) with brand /
 * center / actions slots over a left nav (labeled sidebar or icon rail), a
 * scrollable content region, and an optional docked inspector. Landmarks:
 * banner header, labeled nav, main content, complementary aside. The active
 * nav item uses the blue-tinted selected surface — never volt. Ported from
 * design/tarski-kit/components/studio/AppShell.jsx.
 */
export function AppShell({
  brand,
  center,
  actions,
  navMode = "sidebar",
  nav = [],
  navLabel = "Primary",
  inspector,
  children,
  className,
  style,
}: AppShellProps): JSX.Element {
  const rail = navMode === "rail";
  return (
    <div className={cx("tk-app-shell", className)} style={style}>
      <header className="tk-app-shell__topbar">
        {brand === undefined ? null : <div className="tk-app-shell__brand">{brand}</div>}
        <div className="tk-app-shell__center">{center}</div>
        {actions === undefined ? null : <div className="tk-app-shell__actions">{actions}</div>}
      </header>
      <div className="tk-app-shell__body">
        {nav.length === 0 ? null : (
          <nav
            aria-label={navLabel}
            className={cx("tk-app-shell__nav", `tk-app-shell__nav--${navMode}`)}
          >
            {nav.map((group) => (
              <div
                key={group.id ?? group.label ?? group.items[0]?.id ?? "group"}
                className="tk-app-shell__nav-group"
              >
                {group.label === undefined || rail ? null : (
                  <div className="tk-app-shell__nav-group-label">{group.label}</div>
                )}
                {group.items.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    className={cx(
                      "tk-app-shell__nav-item",
                      item.active === true && "tk-app-shell__nav-item--active",
                    )}
                    aria-current={item.active === true ? "page" : undefined}
                    aria-label={rail ? item.label : undefined}
                    title={rail ? item.label : undefined}
                    onClick={item.onSelect}
                  >
                    {item.icon === undefined ? null : (
                      <span className="tk-app-shell__nav-icon">{item.icon}</span>
                    )}
                    {rail ? null : <span className="tk-app-shell__nav-text">{item.label}</span>}
                  </button>
                ))}
              </div>
            ))}
          </nav>
        )}
        {/* biome-ignore lint/a11y/noNoninteractiveTabindex: WCAG scrollable-region-focusable — the scrollable content pane must be keyboard-reachable */}
        <main className="tk-app-shell__content" tabIndex={0}>
          {children}
        </main>
        {inspector === undefined ? null : (
          <aside aria-label="Inspector" className="tk-app-shell__inspector">
            {inspector}
          </aside>
        )}
      </div>
    </div>
  );
}
